import { z } from 'zod'

import { TeamEfficiencyChartChannel } from 'apps/analytics/constants/AnalyticsInterfaces'

const emptyBackendResponse = z.tuple([])

const totalAnalytics = z.object({
  assigned: z.number().optional(),
  done: z.number().optional(),
})

const teamEfficiencyDateEntryRecord = z.record(
  z.enum(TeamEfficiencyChartChannel),
  z.object({
    conversations_count: z.number().nullable(),
    time: z.number().nullable(),
  }),
)

const responseRateDateEntryRecord = z.record(
  z.enum(TeamEfficiencyChartChannel),
  z.object({
    new: z.number().nullable(),
    answered: z.number().nullable(),
    response_rate: z.number().nullable(),
  }),
)

const baseStatsRequestSchema = z.object({
  period_start: z.string(),
  period_end: z.string(),
})

const conversationRequestSchema = baseStatsRequestSchema.extend({
  agent_ids: z.array(z.number()),
})

const teamEfficiencyRequestSchema = baseStatsRequestSchema.extend({
  agent_id: z.number().nullable(),
})

const getTotalConversationsChartStatsResponseSchema = z.object({
  date_range: z.record(z.string(), totalAnalytics).or(emptyBackendResponse),
  total: totalAnalytics,
})

const getConversationsByTeamMembersChartStatsResponseSchema = z.object({
  agents: z.array(totalAnalytics.extend({ id: z.number() })),
  total: totalAnalytics,
})

const getAverageTimeResponseSchema = z.object({
  max_values: z.record(z.enum(TeamEfficiencyChartChannel), z.number().nullable()),
  date_range: z.record(z.string(), teamEfficiencyDateEntryRecord).or(emptyBackendResponse),
})

const getResponseRateResponseSchema = z.object({
  max_values: z.record(z.enum(TeamEfficiencyChartChannel), z.number().nullable()),
  date_range: z.record(z.string(), responseRateDateEntryRecord).or(emptyBackendResponse),
})

export const LiveChatStatisticsSchemas = {
  getTotalConversationsChartStats: {
    path: z.undefined(),
    query: z.undefined(),
    request: conversationRequestSchema,
    response: z.object({
      data: getTotalConversationsChartStatsResponseSchema.or(emptyBackendResponse),
    }),
  },
  getConversationsByTeamMembersChartStats: {
    path: z.undefined(),
    query: z.undefined(),
    request: conversationRequestSchema,
    response: z.object({
      data: getConversationsByTeamMembersChartStatsResponseSchema.or(emptyBackendResponse),
    }),
  },
  getAverageResponseTime: {
    path: z.undefined(),
    query: z.undefined(),
    request: teamEfficiencyRequestSchema,
    response: z.object({
      data: getAverageTimeResponseSchema.or(emptyBackendResponse),
    }),
  },
  getAverageClosureTime: {
    path: z.undefined(),
    query: z.undefined(),
    request: teamEfficiencyRequestSchema,
    response: z.object({
      data: getAverageTimeResponseSchema.or(emptyBackendResponse),
    }),
  },
  getResponseRate: {
    path: z.undefined(),
    query: z.undefined(),
    request: baseStatsRequestSchema,
    response: z.object({
      data: getResponseRateResponseSchema.or(emptyBackendResponse),
    }),
  },
}

export type ConversationRequest = z.infer<typeof conversationRequestSchema>
export type TeamEfficiencyRequest = z.infer<typeof teamEfficiencyRequestSchema>
export type ResponseRateRequest = z.infer<typeof baseStatsRequestSchema>

export type GetTotalConversationsChartStatsResponse = z.infer<
  typeof getTotalConversationsChartStatsResponseSchema
>
export type GetConversationsByTeamMembersChartStatsResponse = z.infer<
  typeof getConversationsByTeamMembersChartStatsResponseSchema
>
export type GetAverageTimeResponse = z.infer<typeof getAverageTimeResponseSchema>
export type GetResponseRateResponse = z.infer<typeof getResponseRateResponseSchema>
